import { lazy, Suspense, type ComponentType } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  useRouterState,
} from '@tanstack/react-router';
import {
  CardSkeleton,
  NotFoundState,
  Page,
  PageBody,
  PageHeader,
  PermissionDeniedState,
} from '@micarahr/web-ui';
import { AppShell } from '../components/shell/app-shell';
import { permissionsForPath } from './navigation';
import { useAuthStore } from '../stores';
import { parseSearch, stringifySearch } from '../lib/url-state';

/**
 * Routing.
 *
 * Every route is code-split, and every route is guarded by the same permission
 * list the sidebar reads. A user who types a URL they cannot open gets the
 * permission state naming what they would need — not a blank page, and not the
 * data with the navigation hidden.
 */

function Guard({ children }: { children: React.ReactNode }) {
  /*
   * Both the address and the page still on screen.
   *
   * While a navigation is pending, `location` is already the new address and
   * the outlet still renders the previous page. Checking the address alone let
   * that page through for a render — which is all it takes to fire its
   * queries: after one account signed out on /loans and another signed in,
   * the new account's first render of the loans page asked for loans it may
   * not see, and got two 403s.
   */
  const paths = useRouterState({
    select: (state) => [
      state.location.pathname,
      state.resolvedLocation?.pathname ?? state.location.pathname,
    ],
    structuralSharing: true,
  });
  const session = useAuthStore((state) => state.session);
  const granted = new Set(session?.permissions ?? []);

  for (const pathname of paths) {
    const required = permissionsForPath(pathname);
    if (required.length > 0 && !required.some((permission) => granted.has(permission))) {
      return <PermissionDeniedState permission={required[0]} what="this area" />;
    }
  }
  return <>{children}</>;
}

const rootRoute = createRootRoute({
  component: () => (
    <AppShell>
      <Guard>
        <Suspense
          fallback={
            <div className="space-y-3 p-6">
              <CardSkeleton lines={2} />
              <CardSkeleton lines={4} />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </Guard>
    </AppShell>
  ),
});

/** Wrap a lazy import so a failed chunk shows a state rather than a blank screen. */
function page(loader: () => Promise<{ default: ComponentType }>) {
  return lazy(loader);
}

const routes = [
  { path: '/', component: page(() => import('../features/dashboard/overview-page')) },
  { path: '/approvals', component: page(() => import('../features/approvals/approvals-page')) },
  { path: '/employees', component: page(() => import('../features/employees/employees-page')) },
  {
    path: '/employees/new',
    component: page(() => import('../features/employees/create-employee-page')),
  },
  {
    path: '/departments',
    component: page(() => import('../features/departments/departments-page')),
  },
  {
    path: '/work-locations',
    component: page(() => import('../features/locations/locations-page')),
  },
  { path: '/attendance', component: page(() => import('../features/attendance/attendance-page')) },
  {
    path: '/attendance/review',
    component: page(() => import('../features/attendance/review-page')),
  },
  {
    path: '/attendance/corrections',
    component: page(() => import('../features/attendance/corrections-page')),
  },
  {
    path: '/attendance/devices',
    component: page(() => import('../features/devices/devices-page')),
  },
  { path: '/schedules', component: page(() => import('../features/schedules/schedules-page')) },
  { path: '/timesheets', component: page(() => import('../features/timesheets/timesheets-page')) },
  { path: '/leave', component: page(() => import('../features/leave/leave-page')) },
  { path: '/leave/balances', component: page(() => import('../features/leave/balances-page')) },
  { path: '/overtime', component: page(() => import('../features/overtime/overtime-page')) },
  { path: '/payroll', component: page(() => import('../features/payroll/payroll-page')) },
  { path: '/payroll/payslips', component: page(() => import('../features/payroll/payslips-page')) },
  {
    path: '/payroll/configuration',
    component: page(() => import('../features/payroll/configuration-page')),
  },
  {
    path: '/compensation',
    component: page(() => import('../features/compensation/compensation-page')),
  },
  { path: '/benefits', component: page(() => import('../features/benefits/benefits-page')) },
  { path: '/loans', component: page(() => import('../features/loans/loans-page')) },
  {
    path: '/recruitment',
    component: page(() => import('../features/recruitment/recruitment-page')),
  },
  {
    path: '/recruitment/candidates',
    component: page(() => import('../features/recruitment/candidates-page')),
  },
  { path: '/onboarding', component: page(() => import('../features/onboarding/onboarding-page')) },
  { path: '/probation', component: page(() => import('../features/probation/probation-page')) },
  {
    path: '/performance',
    component: page(() => import('../features/performance/performance-page')),
  },
  { path: '/learning', component: page(() => import('../features/learning/learning-page')) },
  { path: '/documents', component: page(() => import('../features/documents/documents-page')) },
  { path: '/policies', component: page(() => import('../features/documents/policies-page')) },
  {
    path: '/announcements',
    component: page(() => import('../features/announcements/announcements-page')),
  },
  { path: '/assets', component: page(() => import('../features/assets/assets-page')) },
  {
    path: '/employee-relations',
    component: page(() => import('../features/relations/relations-page')),
  },
  {
    path: '/offboarding',
    component: page(() => import('../features/offboarding/offboarding-page')),
  },
  { path: '/reports', component: page(() => import('../features/reports/reports-page')) },
  { path: '/audit', component: page(() => import('../features/audit/audit-page')) },
  { path: '/users', component: page(() => import('../features/users/users-page')) },
  { path: '/roles', component: page(() => import('../features/roles/roles-page')) },
  { path: '/settings', component: page(() => import('../features/settings/settings-page')) },
  /*
   * The Simulation Centre injects failures and resets the demonstration data.
   * It exists only outside production builds — the static `import.meta.env`
   * check means production builds neither register the route nor emit the
   * page's chunk; the path falls through to the not-found page.
   */
  ...(import.meta.env.PROD
    ? []
    : [
        {
          path: '/simulation',
          component: page(() => import('../features/simulation/simulation-page')),
        },
      ]),
] as const;

/**
 * Keep whatever search parameters a page put there.
 *
 * Workspace state — search text, filters, page, the open record — is carried in
 * the query string so a view can be refreshed and shared (§43). Every page owns
 * a different set of keys, so rather than declaring them route by route this
 * accepts any string-valued parameter and drops everything else.
 */
function passthroughSearch(search: Record<string, unknown>): Record<string, string> {
  const kept: Record<string, string> = {};
  for (const [key, value] of Object.entries(search)) {
    if (typeof value === 'string' && value.length > 0) kept[key] = value;
    else if (typeof value === 'number' && Number.isFinite(value)) kept[key] = String(value);
    // The query string reads `true` and `false` back as booleans; here they are text again.
    else if (typeof value === 'boolean') kept[key] = String(value);
  }
  return kept;
}

const routeTree = rootRoute.addChildren(
  routes.map((route) =>
    createRoute({
      getParentRoute: () => rootRoute,
      path: route.path,
      component: route.component,
      validateSearch: passthroughSearch,
    }),
  ),
);

/**
 * A mistyped URL should land somewhere, not nowhere.
 *
 * Now that workspace state is carried in the query string, people edit these
 * URLs by hand and paste them to each other — so an unrecognised path is a
 * normal thing to hit, not an edge case. The router's bare default said only
 * "Not Found", outside the shell and with no way back.
 */
function NotFound() {
  return (
    <Page>
      <PageHeader title="Page not found" />
      <PageBody>
        <NotFoundState
          title="There is nothing at this address"
          message="Check the link, or start again from the overview — every page is also in the sidebar."
          onBack={() => void router.navigate({ to: '/' })}
        />
      </PageBody>
    </Page>
  );
}

export const router = createRouter({
  routeTree,
  // Plain text in the address bar — `pageSize=200`, not `pageSize=%22200%22`.
  parseSearch,
  stringifySearch,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  defaultNotFoundComponent: NotFound,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
